import { useKestrelViewerContext } from 'features/kestrelViewer'
import { PhotoViewer } from 'features/kestrelViewer/ui/tools/photoreel/PhotoViewer'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8); /* Darken background */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000; /* Above panels/forms (<=2001), below global modals (10000) */
`

const NavButton = styled.button<{ $position: 'left' | 'right' }>`
  position: absolute;
  ${({ $position }) => $position}: 3rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.8);
  z-index: 10;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.8);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const PhotoCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 10;
`

const ModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  padding: 1rem;
`

interface PhotoReelModalProps {
  photo: KVPhoto
  photos: KVPhoto[]
  onClose: () => void
  onPhotoChange: (photo: KVPhoto) => void
  rotation: number
  onRotationChange: (rotation: number) => void
}

export const PhotoReelModal: React.FC<PhotoReelModalProps> = ({
  photo,
  photos,
  onClose,
  onPhotoChange,
  rotation,
  onRotationChange,
}) => {
  const { setSelectedPhoto } = useKestrelViewerContext()
  const [hasMoved, setHasMoved] = useState(false)

  const currentIndex = photos.findIndex((p) => p.id === photo.id)
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < photos.length - 1

  const navigateToPrevious = useCallback(() => {
    if (hasPrevious) {
      onPhotoChange(photos[currentIndex - 1])
    }
  }, [hasPrevious, currentIndex, photos, onPhotoChange])

  const navigateToNext = useCallback(() => {
    if (hasNext) {
      onPhotoChange(photos[currentIndex + 1])
    }
  }, [hasNext, currentIndex, photos, onPhotoChange])

  // Event handlers
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !hasMoved) {
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
  }

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleCloseClick = () => {
    setSelectedPhoto(null)
  }

  // Handle escape key to close modal and arrow keys to navigate
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        navigateToPrevious()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        navigateToNext()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, navigateToPrevious, navigateToNext])

  return (
    <ModalOverlay
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}>
      {photos.length > 1 && (
        <>
          <NavButton
            $position='left'
            onClick={(e) => {
              e.stopPropagation()
              navigateToPrevious()
            }}
            disabled={!hasPrevious}
            aria-label='Previous photo'>
            <ChevronLeft size={24} />
          </NavButton>
          <NavButton
            $position='right'
            onClick={(e) => {
              e.stopPropagation()
              navigateToNext()
            }}
            disabled={!hasNext}
            aria-label='Next photo'>
            <ChevronRight size={24} />
          </NavButton>
        </>
      )}

      <ModalContent>
        <PhotoViewer
          photo={photo}
          rotation={rotation}
          onRotationChange={onRotationChange}
          fillContainer={false}
          compactToolbar={false}
          showCrosshair={true}
          onPhotoChange={() => setHasMoved(false)}
          onClose={handleCloseClick}
        />

        {photos.length > 1 && (
          <PhotoCounter>
            {currentIndex + 1} / {photos.length}
          </PhotoCounter>
        )}
      </ModalContent>
    </ModalOverlay>
  )
}
